// units.ts — calculation unit of a line and of a quote item.
//
// This module prices nothing: it describes what the price was computed on. Items whose base
// depends on a human choice (per file vs per time) read that choice rather than a fixed table.
// The five codes are verbatim and are never abbreviated differently.

import type { QuoteLine } from "./core";
import {
  isLargeFormatPrintRegime, isAccessoryMlRegime, isCustomPrintRegime, isUnitRegime,
  isTierRegime, isJoineryFabRegime, isTransferRegime, isServiceRegime,
} from "./regimes";
import type { ServicesBlock } from "../products/services-pricing";

/** The five unit codes, verbatim. */
export type CalcUnit = "Unit" | "MLin" | "M2" | "T/Hr" | "J/Hm";

/** i18n key of what each code means, for tooltips — same source as the displayed code. */
export const UNIT_LABEL_KEY: Readonly<Record<CalcUnit, string>> = {
  Unit: "engineUnitUnit",
  MLin: "engineUnitMLin",
  M2: "engineUnitM2",
  "T/Hr": "engineUnitHour",
  "J/Hm": "engineUnitPersonDay",
};

/**
 * Unit of a quote line, by regime. SUR_PRINT is M2 even though its total is entered by hand,
 * because its base is still surface; SVC is T/Hr since its quantity column holds hours.
 *
 * @param l The quote line.
 * @returns The calculation unit code.
 */
export function lineUnit(l: QuoteLine): CalcUnit {
  if (isLargeFormatPrintRegime(l) || isCustomPrintRegime(l)) return "M2";
  if (isAccessoryMlRegime(l)) return "MLin";
  if (isServiceRegime(l)) return "T/Hr";
  // STAND, TRANSFER, MENUIS & FAB and unit custom sizes are all per piece.
  if (isTierRegime(l) || isTransferRegime(l) || isJoineryFabRegime(l) || isUnitRegime(l)) return "Unit";
  return "Unit";
}

/**
 * Unit of a quote footer item, by its key and by the selected mode when it has one.
 * Keys are the ones `computeServices` sets, so renaming a display label cannot change a unit.
 *
 * @param key The footer item key.
 * @param servicesBlock The services block, read for the studio items that carry a per-file / per-time mode.
 * @returns The calculation unit code.
 */
export function serviceUnit(key: string, servicesBlock: ServicesBlock = {}): CalcUnit {
  switch (key) {
    // Studio: two items carry a mode, so the unit follows the human choice.
    case "info":
      return servicesBlock.infoMode === "hourly" ? "T/Hr" : "Unit"; // per file = per piece
    case "packaging":
      return servicesBlock.packagingMode === "hourly" ? "T/Hr" : "Unit"; // per element = per piece
    case "advancedInfo":
      return "T/Hr"; // advanced graphics is always time-based (no mode)
    case "gp":
      // Project management is billed per day, in half-day steps: person-time, not hourly service.
      return "J/Hm";

    // Install: person-day, which is what the J/Hm code names.
    case "installation":
    case "removal":
      return "J/Hm";

    // Survey: a flat yes / no, one piece.
    case "survey":
      return "Unit";

    // Install equipment: its quantity is already m².
    case "installEquipment":
      return "M2";

    // Everything else counts per piece: logistics, rental, tests, calibration, waste.
    default:
      return "Unit";
  }
}
